import React, {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";

import SizeBox from "../../../../../../components/SizeBox";
import TableCustom from "../../../../../../components/Table";
import ButtonCustom from "../../../../../../components/Button";
import SearchBar4Table from "../../../../../../components/SearchBar4Table";
import { LocalizationContext } from "../../../../../../locales/Translation";

import { propertyDetailSelector } from "../../../../selectors/PropertySelector";
import { deskimoModalOfficeAction } from "../../../../actions/PropertyActions";
import { CREATE_TYPE } from "../../../../configs/Constants";
import {
  ORDER_BY_ASC,
  ORDER_BY_DESC,
  ORDER_BY_NONE,
} from "../../../../../App/configs/Constants";

import ModalCreateEditOffice from "./components/ModalCreateEditOffice";
import { OfficeListItem, TableColumn } from "./types";
import { SEARCH_DEBOUNCE_DELAY } from "./constants";
import { useOfficeData, useOfficeFilter } from "./hooks";
import FilterStatus from "./components/FilterStatus";
import ActionsButton from "./components/ActionsButton";

export default function DayOfficeTab() {
  const { translations } = useContext(LocalizationContext);
  const { id: propertyId, companyId } = useParams<{
    id: string;
    companyId: string;
  }>();
  const dispatch = useDispatch();
  const propertyDetailData = useSelector(propertyDetailSelector);

  if (!propertyId || !companyId) {
    return <div>Error: Missing required parameters</div>;
  }

  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const { filter, updateFilter } = useOfficeFilter(propertyId, companyId);
  const { data: officeList, loading } = useOfficeData(filter, refreshTrigger);

  const handleRefresh = useCallback(() => {
    setRefreshTrigger((prev) => !prev);
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce((keyword: string) => {
        updateFilter({ keyword, page: 1 }); // Reset to first page on search
      }, SEARCH_DEBOUNCE_DELAY),
    [updateFilter]
  );

  const handleSearchChange = useCallback(
    (_e: unknown, { value }: { value: string }) => {
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleFilterChange = useCallback(
    (_e: SyntheticEvent, data: { checked: boolean; value: number }) => {
      const { checked, value } = data;

      updateFilter({
        statuses: checked
          ? [...filter.statuses, value]
          : filter.statuses.filter((status) => status !== value),
        page: 1, // Reset to first page on filter change
      });
    },
    [filter.statuses, updateFilter]
  );

  const handlePaginationChange = useCallback(
    (_e: unknown, { activePage }: { activePage: number }) => {
      updateFilter({ page: activePage });
    },
    [updateFilter]
  );

  const handleSortChange = useCallback(
    (column: TableColumn) => {
      if (!column.key) return;

      const isSameColumn = filter.sort_by === column.key;
      let newOrderBy = ORDER_BY_ASC;

      if (isSameColumn) {
        switch (filter.order_by) {
          case ORDER_BY_NONE:
            newOrderBy = ORDER_BY_ASC;
            break;
          case ORDER_BY_ASC:
            newOrderBy = ORDER_BY_DESC;
            break;
          case ORDER_BY_DESC:
            newOrderBy = ORDER_BY_NONE;
            break;
          default:
            newOrderBy = ORDER_BY_ASC;
        }
      }

      updateFilter({
        sort_by: column.key,
        order_by: newOrderBy,
      });
    },
    [filter.sort_by, filter.order_by, updateFilter]
  );

  const handleCreateOffice = useCallback(() => {
    dispatch(
      deskimoModalOfficeAction({
        isOpen: true,
        type: CREATE_TYPE,
      })
    );
  }, [dispatch]);

  const tableData = useMemo(() => {
    return (
      officeList?.day_offices?.map((office: OfficeListItem) => ({
        name: office.name,
        capacity: `${office.capacity} people`,
        // available_for_daily_booking: Object.prototype.hasOwnProperty.call(
        //   office,
        //   "available_for_daily_booking"
        // )
        //   ? office.is_available_for_daily_booking
        //     ? "Available"
        //     : "Unavailable"
        //   : "N/A",
        // daily_rate: office.daily_rate ? office.daily_rate : "-",
        daily_rate: office.daily_rate ? office.daily_rate : "-",
        hourly_rate: office.hourly_rate ? office.hourly_rate : "-",
        monthly_rate: office.monthly_rate ? office.monthly_rate : "-",
        status: office.status === 1 ? "Published" : "Unpublished",
        col6: (
          <ActionsButton
            visibilityAction={office.visibility_action}
            visibility={office.visibility}
            id={office.id}
            companyId={companyId}
            status={office.status}
            isIWG={propertyDetailData?.is_iwg}
            onRefresh={handleRefresh}
          />
        ),
      })) || []
    );
  }, [officeList?.day_offices, handleRefresh]);

  const columns = useMemo<TableColumn[]>(
    () => [
      { key: "name", col: "Office Name" },
      { key: "capacity", col: translations.capacity },
      // {
      //   key: "available_for_daily_booking",
      //   col: translations.availableForDailyBooking,
      // },
      { key: "daily_rate", col: translations.dailyRate },
      { key: "hourly_rate", col: translations.hourlyRate },
      { key: "monthly_rate", col: translations.monthlyRate },
      { key: "status", col: translations.status },
      { key: null, col: translations.action },
      // {
      //   id: 7,
      //   name: "Day Office 1",
      //   assign_PMS_room: true,
      //   capacity: 5,
      //   hourly_rate: "SGD 10.00",
      //   status: "1: published",
      //   code: "SYLXVMK4",
      //   token: "SYLXVMK4",
      //   property_name: "Moonlight",
      //   company_name: "Navi Asia",
      //   company_id: 27,
      // },
    ],
    [translations]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <>
      <header className="meeting-room-header">
        <div className="property-name-header">
          {propertyDetailData.name || ""}
        </div>
        <div className="button-groups">
          <ButtonCustom
            onClick={handleCreateOffice}
            className="outline-primary manage-pms-cta"
            height="36px"
          >
            <>
              Add New Office
              <i className="uil uil-plus" />
            </>
          </ButtonCustom>
        </div>
      </header>

      <SizeBox height="32px" />

      <section className="filter-section">
        <SearchBar4Table
          placeholder="Search by office name"
          onChange={handleSearchChange}
        />
        <div className="dropdown-filter d-flex">
          <FilterStatus
            filters={filter.statuses}
            onChangeFilter={handleFilterChange}
          />
        </div>
      </section>

      <TableCustom
        loading={loading}
        showTotal
        sortTable
        orderBy={filter.order_by}
        sortBy={filter.sort_by}
        onChangeSortCol={handleSortChange}
        tableData={tableData}
        columns={columns}
        currentPage={filter.page}
        totalItems={officeList?.pagination?.total || 0}
        unit="offices"
        onChangePagination={handlePaginationChange}
        tableClass="table-meeting-room"
      />

      <ModalCreateEditOffice onRefresh={handleRefresh} />
    </>
  );
}
